/**
 * Per-page notification settings card (simplified UI) — toggle, location
 * chips, and page-specific advanced sections (trucking calendar types).
 */

import React from 'react';
import type {
  NotificationPageId,
  NotificationPageConfig,
} from '../../../config/notificationPageRegistry';
import type { PageNotificationSettings } from '@/types/notification.types';
import type { Location } from '../../../config/firebase';
import type { StatusTransition } from '../utils/settingsLogic';
import { TruckingCalendarAdvancedSettings } from './TruckingCalendarAdvancedSettings';

interface NotificationPageCardProps {
  pageConfig: NotificationPageConfig;
  pageSettings: PageNotificationSettings | undefined;
  accessibleLocations: string[];
  locations: Location[];
  isEnabled: boolean;
  /** True when the master notification toggle is off */
  isDisabled: boolean;
  locationSummary: string;
  isLocationsExpanded: boolean;
  isAdvancedExpanded: boolean;
  onTogglePage: (pageId: NotificationPageId) => void;
  onToggleLocationsExpanded: (pageId: NotificationPageId) => void;
  onToggleAdvancedExpanded: (pageId: NotificationPageId) => void;
  onToggleLocation: (pageId: NotificationPageId, locationId: string) => void;
  onToggleAllLocations: (pageId: NotificationPageId) => void;
  onSetReminderMinutes: (pageId: NotificationPageId, minutes: number) => void;
  onToggleReminderEnabled: (pageId: NotificationPageId) => void;
  onToggleNewEventEnabled: (pageId: NotificationPageId) => void;
  onToggleStatusChangeEnabled: (pageId: NotificationPageId) => void;
  onToggleStatusTransition: (pageId: NotificationPageId, transition: StatusTransition) => void;
}

export const NotificationPageCard: React.FC<NotificationPageCardProps> = ({
  pageConfig,
  pageSettings,
  accessibleLocations,
  locations,
  isEnabled,
  isDisabled,
  locationSummary,
  isLocationsExpanded,
  isAdvancedExpanded,
  onTogglePage,
  onToggleLocationsExpanded,
  onToggleAdvancedExpanded,
  onToggleLocation,
  onToggleAllLocations,
  onSetReminderMinutes,
  onToggleReminderEnabled,
  onToggleNewEventEnabled,
  onToggleStatusChangeEnabled,
  onToggleStatusTransition,
}) => {
  const { id, label, icon, color, comingSoon } = pageConfig;

  // If user has no access to this page's locations, don't show it
  if (accessibleLocations.length === 0) return null;

  // Color variants for the icon
  const iconColors: Record<string, string> = {
    orange: 'text-hue-orange-deep',
    green: 'text-hue-green-deep',
    purple: 'text-hue-purple-deep',
  };
  const iconColor = iconColors[color] || iconColors.orange;

  // Color for chips
  const chipColors: Record<string, string> = {
    orange: 'bg-hue-orange-soft text-hue-orange-strong ring-hue-orange-border-strong',
    green: 'bg-hue-green-soft text-hue-green-strong ring-hue-green-border-strong',
    purple: 'bg-hue-purple-soft text-hue-purple-strong ring-hue-purple-border-strong',
  };
  const chipColor = chipColors[color] || chipColors.orange;

  return (
    <div
      className={`bg-surface rounded-lg border ${
        isEnabled ? 'border-line-strong' : 'border-line'
      } overflow-hidden ${comingSoon ? 'opacity-60' : ''}`}
    >
      {/* Compact header with toggle */}
      <div className="flex items-center gap-3 p-3">
        <i className={`fa-solid ${icon} ${iconColor} text-lg w-5`}></i>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`font-medium ${isEnabled ? 'text-ink' : 'text-ink-body'}`}>
              {label}
            </span>
            {comingSoon && (
              <span className="text-[10px] bg-surface-strong text-ink-muted px-1.5 py-0.5 rounded">
                Soon
              </span>
            )}
          </div>
          {isEnabled && (
            <p className="text-xs text-ink-muted truncate">{locationSummary}</p>
          )}
        </div>
        {!comingSoon && (
          <button
            onClick={() => onTogglePage(id)}
            disabled={isDisabled}
            className={`relative w-11 h-6 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-focus focus:ring-offset-1 ${
              isEnabled ? 'bg-selected' : 'bg-surface-deep'
            } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-5 h-5 bg-surface rounded-full shadow-sm transition-transform ${
                isEnabled ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        )}
      </div>

      {/* Expandable sections when enabled */}
      {isEnabled && !comingSoon && (
        <div className="border-t border-line-subtle">
          {/* Customize locations (collapsed by default) */}
          <button
            onClick={() => onToggleLocationsExpanded(id)}
            className="w-full flex items-center justify-between px-3 py-2 text-xs text-ink-body hover:bg-surface-muted transition-colors"
          >
            <span className="flex items-center gap-1.5">
              <i className="fa-solid fa-location-dot w-3"></i>
              Customize locations
            </span>
            <i className={`fa-solid fa-chevron-down text-[10px] transition-transform ${isLocationsExpanded ? 'rotate-180' : ''}`}></i>
          </button>

          {isLocationsExpanded && (
            <div className="px-3 pb-3 space-y-2">
              <div className="flex justify-end">
                <button
                  onClick={() => onToggleAllLocations(id)}
                  className="text-xs text-link hover:text-link-hover font-medium"
                  disabled={isDisabled}
                >
                  {(pageSettings?.locations?.length || 0) === accessibleLocations.length
                    ? 'Deselect all'
                    : 'Select all'}
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {accessibleLocations.map((locId) => {
                  const location = locations.find((l) => l.id === locId);
                  if (!location) return null;
                  const isChecked = pageSettings?.locations?.includes(locId) || false;
                  return (
                    <label
                      key={locId}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium cursor-pointer transition-all ${
                        isChecked
                          ? `${chipColor} ring-1`
                          : 'bg-surface-inset text-ink-muted hover:bg-surface-strong'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={isChecked}
                        onChange={() => onToggleLocation(id, locId)}
                        disabled={isDisabled}
                      />
                      {isChecked && <i className="fa-solid fa-check text-[8px]"></i>}
                      {location.name}
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* Trucking Calendar advanced settings (collapsed by default) */}
          {id === 'truckingCalendar' && (
            <TruckingCalendarAdvancedSettings
              pageSettings={pageSettings}
              isDisabled={isDisabled}
              isExpanded={isAdvancedExpanded}
              onToggleExpanded={() => onToggleAdvancedExpanded(id)}
              onSetReminderMinutes={(minutes) => onSetReminderMinutes(id, minutes)}
              onToggleReminderEnabled={() => onToggleReminderEnabled(id)}
              onToggleNewEventEnabled={() => onToggleNewEventEnabled(id)}
              onToggleStatusChangeEnabled={() => onToggleStatusChangeEnabled(id)}
              onToggleStatusTransition={(transition) => onToggleStatusTransition(id, transition)}
            />
          )}
        </div>
      )}
    </div>
  );
};
